import { createStore } from '@xstate/store';
import type { BoardRequirement } from '../../types';

// ============================================
// Board Requirements Store - Pro Board Requirements
// ============================================

const PERSISTENCE_DEBOUNCE_MS = 500;

export interface BoardRequirementsContext {
  requirements: BoardRequirement[];
}

function getLocalStorageKey(boardId: string): string {
  return `roadmap_2026_board_${boardId}_requirements`;
}

function loadRequirementsFromLocalStorage(storageKey: string): BoardRequirement[] {
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return [];

    const parsed = JSON.parse(saved) as BoardRequirement[];
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveRequirementsToLocalStorage(storageKey: string, requirements: BoardRequirement[]): void {
  localStorage.setItem(storageKey, JSON.stringify(requirements));
}

function createBoardRequirementsStoreInstance(storageKey: string) {
  const initialRequirements = loadRequirementsFromLocalStorage(storageKey);

  const store = createStore({
    context: {
      requirements: initialRequirements,
    } as BoardRequirementsContext,

    on: {
      'boardRequirement.add': (ctx, event: { requirement: BoardRequirement }) => {
        if (ctx.requirements.some(r => r.text === event.requirement.text)) {
          console.warn(`[boardRequirementsStore] Requirement "${event.requirement.text}" already exists`);
          return ctx;
        }
        return {
          ...ctx,
          requirements: [...ctx.requirements, event.requirement],
        };
      },

      'boardRequirement.update': (ctx, event: { text: string; updates: Partial<BoardRequirement> }) => {
        const reqIndex = ctx.requirements.findIndex(r => r.text === event.text);
        if (reqIndex === -1) {
          console.warn(`[boardRequirementsStore] Requirement "${event.text}" not found`);
          return ctx;
        }

        if (event.updates.text && event.updates.text !== event.text) {
          const existingWithText = ctx.requirements.find(
            r => r.text === event.updates.text && r.text !== event.text
          );
          if (existingWithText) {
            console.warn(`[boardRequirementsStore] Text "${event.updates.text}" is already used`);
            return ctx;
          }
        }

        const updatedRequirements = [...ctx.requirements];
        updatedRequirements[reqIndex] = {
          ...updatedRequirements[reqIndex],
          ...event.updates,
        };

        return {
          ...ctx,
          requirements: updatedRequirements,
        };
      },

      'boardRequirement.delete': (ctx, event: { text: string }) => {
        const reqIndex = ctx.requirements.findIndex(r => r.text === event.text);
        if (reqIndex === -1) {
          console.warn(`[boardRequirementsStore] Requirement "${event.text}" not found`);
          return ctx;
        }
        return {
          ...ctx,
          requirements: ctx.requirements.filter(r => r.text !== event.text),
        };
      },

      'boardRequirements.reset': () => ({
        requirements: [],
      }),

      'boardRequirements.import': (_ctx, event: { requirements: BoardRequirement[] }) => ({
        requirements: event.requirements,
      }),
    },
  });

  // Debounced persistence
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastRequirementsHash = '';

  function computeRequirementsHash(requirements: BoardRequirement[]): string {
    return requirements.map(r => `${r.text}:${r.type}:${r.description}`).join('|');
  }

  store.subscribe((snapshot) => {
    const currentHash = computeRequirementsHash(snapshot.context.requirements);
    if (currentHash === lastRequirementsHash) return;
    lastRequirementsHash = currentHash;

    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => {
      saveRequirementsToLocalStorage(storageKey, snapshot.context.requirements);
    }, PERSISTENCE_DEBOUNCE_MS);
  });

  return store;
}

// ============================================
// Board-specific Store Instances
// ============================================

type BoardRequirementsStoreInstance = ReturnType<typeof createBoardRequirementsStoreInstance>;

const storeInstances = new Map<string, BoardRequirementsStoreInstance>();

export function initBoardRequirementsStore(boardId: string): BoardRequirementsStoreInstance {
  const existing = storeInstances.get(boardId);
  if (existing) return existing;

  const storageKey = getLocalStorageKey(boardId);
  const store = createBoardRequirementsStoreInstance(storageKey);
  storeInstances.set(boardId, store);
  return store;
}

export function getBoardRequirementsStore(boardId: string): BoardRequirementsStoreInstance {
  const instance = storeInstances.get(boardId);
  if (!instance) {
    throw new Error(`Board requirements store for board "${boardId}" not initialized.`);
  }
  return instance;
}

export function disposeBoardRequirementsStore(boardId: string): void {
  storeInstances.delete(boardId);
}

// ============================================
// Helper-Funktionen
// ============================================

export function getBoardRequirementsArray(boardId: string): BoardRequirement[] {
  return getBoardRequirementsStore(boardId).getSnapshot().context.requirements;
}

export function getBoardRequirementByText(boardId: string, text: string): BoardRequirement | undefined {
  return getBoardRequirementsArray(boardId).find(r => r.text === text);
}

export type BoardRequirementsStore = BoardRequirementsStoreInstance;
